/**
 * GuidePlane — smart-guide lines for an engaged object snap.
 *
 * Fixed-shape plane: exactly one vertical + one horizontal line whose
 * geometry AND visibility derive from shared values the drag worklet writes
 * per frame. A variable-count guide list would force React reconciliation
 * mid-drag — two always-mounted lines with derived opacity cost nothing.
 *
 * @module floorplan/renderer/GuidePlane
 * @category FloorPlan
 */

import type { SharedValue } from 'react-native-reanimated';
import { useDerivedValue } from 'react-native-reanimated';
import { Line } from '@shopify/react-native-skia';

/** Shared-value bundle the drag worklet writes (one line per axis). */
export type GuideChannels = {
  /** Vertical guide (a snap on the x axis): 1 = visible. */
  xActive: SharedValue<number>;
  xPosition: SharedValue<number>;
  xFrom: SharedValue<number>;
  xTo: SharedValue<number>;
  /** Horizontal guide (a snap on the y axis). */
  yActive: SharedValue<number>;
  yPosition: SharedValue<number>;
  yFrom: SharedValue<number>;
  yTo: SharedValue<number>;
};

type Props = {
  guides: GuideChannels;
  /** Guide color (theme.accent). */
  color: string;
  /** Zoom snapshot for constant-ish screen stroke width. */
  zoom: number;
};

const GuidePlane = ({ guides, color, zoom }: Props) => {
  // Plain `{x, y}` literals, NOT Skia's `vec()`. `vec()` returns a
  // `Skia.Point` — a JSI-backed object (`__typename__` / `__box` / `dispose`
  // on its prototype) that react-native-worklets cannot copy, and a
  // `useDerivedValue` result HAS to cross into the UI runtime. Serializing it
  // threw `[Worklets] Cannot copy value of type \`Object\`` during Skia's
  // commit, which took down the floor-plan editor and the label designer
  // (Sentry FUZE-STORE-APP-4Q0 / -4Q5). Skia's `Vector` is the structural
  // interface `{ readonly x: number; readonly y: number }`, so a plain object
  // is the same thing to `<Line>` minus the native handle.
  const xP1 = useDerivedValue(() => ({
    x: guides.xPosition.get(),
    y: guides.xFrom.get(),
  }));
  const xP2 = useDerivedValue(() => ({
    x: guides.xPosition.get(),
    y: guides.xTo.get(),
  }));
  const xOpacity = useDerivedValue(() => guides.xActive.get());
  const yP1 = useDerivedValue(() => ({
    x: guides.yFrom.get(),
    y: guides.yPosition.get(),
  }));
  const yP2 = useDerivedValue(() => ({
    x: guides.yTo.get(),
    y: guides.yPosition.get(),
  }));
  const yOpacity = useDerivedValue(() => guides.yActive.get());

  const strokeWidth = 1.5 / zoom;

  return (
    <>
      <Line
        p1={xP1}
        p2={xP2}
        color={color}
        strokeWidth={strokeWidth}
        opacity={xOpacity}
      />
      <Line
        p1={yP1}
        p2={yP2}
        color={color}
        strokeWidth={strokeWidth}
        opacity={yOpacity}
      />
    </>
  );
};

export default GuidePlane;
